/**
 * One row of the multi-row FK Breadcrumb tree.
 *
 * Row 0 is the tree spine (root → `childIds[0]` → … → leaf); off-spine rows
 * render their own first-child spine. The L-shaped branch arrows that
 * connect each off-spine row to its fork-parent are NOT drawn inline here;
 * they are emitted by the absolutely-positioned `<BranchArrowsOverlay />`
 * sibling, which measures the actual DOM rects of the source and target
 * chips and draws one SVG path per branch. That keeps the arrow's vertical
 * leg visually anchored to the fork-parent chip even under deep nesting
 * where the off-spine row's leading column doesn't sit directly under the
 * source chip.
 *
 * For non-spine rows the leading "spacer" is still built from the path
 * **from root to the fork-parent (inclusive)** rendered as invisible chip
 * placeholders + separators. This makes the branched row's first VISIBLE
 * chip land directly under the column on row 0 that comes right AFTER the
 * fork-parent — i.e. sibling rows align with their first siblings' depth.
 * The connector slot after the placeholders reserves chevron-width space
 * so chip x-positions don't shift; it carries the testid the overlay and
 * e2e tests anchor on but no longer renders an inline glyph.
 *
 * Activation is independent of layout: any chip on any row can be active;
 * the active chip is highlighted in place rather than reshuffled to row 0.
 *
 * Horizontal overflow: each row is `flex` (default nowrap). When the chip
 * trail outgrows the breadcrumb's viewport, the parent `<nav>` scrolls
 * horizontally — there is no per-row wrapping. The overlay SVG mounts inside
 * the scroll content so the arrows scroll with the chips.
 *
 * `<ChevronArrow />` (the same-row separator) remains an inline SVG so the
 * icon rendering stays consistent across platforms (the prior Unicode glyph
 * `›` rendered with noticeable font drift).
 */

import { useMemo } from 'react';
import { Pencil, X } from 'lucide-react';
import { FkBreadcrumbChip } from './FkBreadcrumbChip';
import { chipLabel } from './chipLabel';
import type { TreeLayoutRow } from './breadcrumbTreeLayout';
import type {
  NavigationNode,
  NavigationNodeId,
  NavigationTree,
} from '../../../core/types/workspaceTypes';
import type { JoinTableHint } from '../../../single-row/utils/joinTableHintTypes';

type FkBreadcrumbTreeRowProps = {
  readonly tree: NavigationTree;
  readonly row: TreeLayoutRow;
  /**
   * Whether this row carries a leading branch-arrow connector slot. Row 0
   * (the active line / spine) renders no connector; every off-spine row
   * renders the chevron-width placeholder slot whose data-testid the overlay
   * and e2es anchor on. The visible down-branch arrow itself is drawn by
   * `<BranchArrowsOverlay />`.
   */
  readonly hasConnector: boolean;
  /**
   * Node id of the chip the user just clicked but whose store update is still
   * deferred for one paint frame. The chip with this id renders with the
   * "active" pill styling immediately so click feedback is snappy.
   */
  readonly pendingActivateId?: NavigationNodeId | null;
  readonly onActivate: (id: NavigationNodeId) => void;
  readonly onDismiss: (id: NavigationNodeId) => void;
  readonly onSetJoinTableHint?: (args: {
    schema: string;
    table: string;
    hint: JoinTableHint | null;
  }) => void;
};

/** Drawn right-pointing arrow used as the same-row separator between chips. */
function ChevronArrow() {
  return (
    <svg
      viewBox="0 0 12 12"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-muted-foreground/70"
    >
      <path d="M3 6 L8 6" />
      <path d="M6 3 L9 6 L6 9" />
    </svg>
  );
}

export function FkBreadcrumbTreeRow({
  tree,
  row,
  hasConnector,
  pendingActivateId = null,
  onActivate,
  onDismiss,
  onSetJoinTableHint,
}: FkBreadcrumbTreeRowProps) {
  const activePathSet = activePathIdSet(tree);
  const handleSetHint = (node: NavigationNode, hint: JoinTableHint | null) => {
    if (node.kind !== 'table') return;
    onSetJoinTableHint?.({ schema: node.schema, table: node.table, hint });
  };

  const nodes = row.row
    .map((id) => tree.nodes[id])
    .filter((n): n is NavigationNode => Boolean(n));

  // Path from root → fork-parent (inclusive). Rendered as invisible placeholders
  // so the visible chips on this row align with the column ON ROW 0 that comes
  // immediately after the fork-parent. Empty for spine rows.
  const pathToForkParent = useMemo<NavigationNode[]>(() => {
    if (row.isSpine || row.forkParentId === null) return [];
    const path: NavigationNode[] = [];
    let cur: NavigationNode | undefined = tree.nodes[row.forkParentId];
    while (cur) {
      path.unshift(cur);
      if (cur.parentId === null) break;
      cur = tree.nodes[cur.parentId];
    }
    return path;
  }, [tree, row.isSpine, row.forkParentId]);

  return (
    <div
      className="flex items-center gap-1.5"
      data-testid="fk-breadcrumb-row"
      data-is-spine={row.isSpine ? 'true' : 'false'}
      data-fork-parent-id={row.forkParentId ?? ''}
    >
      {pathToForkParent.map((placeholder, idx) => (
        <span
          key={`placeholder-${placeholder.id}`}
          className="inline-flex items-center gap-1.5 invisible select-none"
          aria-hidden="true"
        >
          {idx > 0 && (
            <span className="inline-flex items-center flex-shrink-0">
              <ChevronArrow />
            </span>
          )}
          <ChipPlaceholder node={placeholder} />
        </span>
      ))}
      {hasConnector && (
        // Branch-arrow connector slot — reserves chevron-width space on every
        // off-spine row so the row's first visible chip stays column-aligned
        // with row 0's chip-after-fork-parent. The visible L-arrow itself is
        // drawn by `<BranchArrowsOverlay />` (sibling of the row container);
        // it measures the source and target chip rects so the leg lands
        // visually under the fork-parent chip even with deep nesting. The
        // testid is keyed by `forkParentId` so e2es can assert which
        // fork-parent this branch came from.
        <span
          className="inline-flex items-center flex-shrink-0 select-none"
          aria-hidden="true"
          data-testid={
            row.forkParentId
              ? `fk-breadcrumb-fork-arrow-${row.forkParentId}`
              : 'fk-breadcrumb-connector'
          }
        >
          <span className="invisible inline-flex items-center"><ChevronArrow /></span>
        </span>
      )}
      {nodes.map((node, idx) => (
        <span
          key={node.id}
          className="inline-flex items-center gap-1.5"
        >
          {idx > 0 && (
            // Invisible chevron-width placeholder — preserves the 12px slot
            // between same-row chips so chip x-positions stay stable. The
            // VISIBLE same-row arrow is drawn by `<BranchArrowsOverlay />`
            // with the same chevron geometry as the down-branch arrows, so
            // every horizontal arrow in the breadcrumb looks identical
            // whether the source chip branches or not.
            <span
              className="invisible inline-flex items-center flex-shrink-0 select-none"
              aria-hidden="true"
            >
              <ChevronArrow />
            </span>
          )}
          <FkBreadcrumbChip
            node={node}
            isActive={node.id === tree.activeNodeId}
            isOnActivePath={activePathSet.has(node.id)}
            isPendingActivate={node.id === pendingActivateId}
            onActivate={onActivate}
            onDismiss={onDismiss}
            onSetJoinTableHint={
              node.kind === 'table' && onSetJoinTableHint
                ? (h) => handleSetHint(node, h)
                : undefined
            }
          />
        </span>
      ))}
    </div>
  );
}

/**
 * Invisible chip-shaped placeholder. Mirrors the visible chip's box-model so
 * widths match exactly — including the per-node edit-mode pencil glyph that
 * `<FkBreadcrumbChip>` adds for TableNodes with active edit state. Without the
 * pencil reservation here the off-spine row's first visible chip drifts left
 * by ~16px, detaching the L-arrow's chevron tip from its horizontal leg.
 *
 * No `data-testid` — must NOT be selectable by the breadcrumb tests that
 * count visible chips. `data-fk-chip-placeholder-id` is a separate attribute
 * dedicated to alignment regression tests.
 */
function ChipPlaceholder({ node }: { node: NavigationNode }) {
  // Mirror the edit-glyph decision from `<FkBreadcrumbChip>` exactly.
  const isInEditMode =
    node.kind === 'table' &&
    ((node.isEditMode ?? false) || (node.pendingChanges?.length ?? 0) > 0);
  return (
    <span
      className="inline-flex items-center gap-1 pl-2 pr-0.5 py-0.5 rounded-full border border-transparent text-[11px]"
      data-fk-chip-placeholder-id={node.id}
    >
      {isInEditMode && (
        <Pencil className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
      )}
      <span className="truncate max-w-[180px]">{chipLabel(node)}</span>
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full">
        <X className="h-3 w-3" aria-hidden="true" />
      </span>
    </span>
  );
}

function activePathIdSet(tree: NavigationTree): ReadonlySet<NavigationNodeId> {
  const set = new Set<NavigationNodeId>();
  let cur: NavigationNode | undefined = tree.nodes[tree.activeNodeId];
  while (cur) {
    set.add(cur.id);
    if (cur.parentId === null) break;
    cur = tree.nodes[cur.parentId];
  }
  return set;
}
